import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import { AccountButton } from '../account-button/AccountButton';
import { AccountMenu } from '../account-menu/AccountMenu';
import { AccountWishList } from '../account-wishlist/AccountWishList';
import { formatGreeting } from '../../utils/user/format-greeting';
import {
  isMin1280pxWide,
  isTouchDevice,
} from '../../utils/device/identifyDevice';
import { UserContext } from '../../scaffold/UserContext';
import { getAccountLinks } from '../links';
import { notifyNavAnalytics } from '../navigation/notify-analytics';

import styles from './account-links.scss';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';

export const AccountLinks = ({
  lgOnlyClassname,
}: {
  lgOnlyClassname?: string;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    envVars: { accountBaseUrl, myAccountUrl },
    features,
  } = useContext(EnvVarsFeaturesContext);
  const {
    userState: {
      user: { signedIn, greeting, notifications },
    },
  } = useContext(UserContext);

  const shouldHideAccount = features.mvoMode || features.hideAccountIcon;
  const shouldHideWishList = features.mvoMode || features.hideWishListIcon;

  const isTouchInputDevice = () => {
    try {
      return isTouchDevice();
    } catch {
      return false;
    }
  };

  const openMenu = () => {
    setIsMenuOpen(true);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleMenu = (
    e: React.MouseEvent<HTMLDivElement | HTMLLIElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    setIsMenuOpen(previousIsMenuOpen => !previousIsMenuOpen);
  };

  const handleOnClickSignedOut = () => {
    if (!isMin1280pxWide()) {
      notifyNavAnalytics('mv', 'Account');
    }
  };

  const handleOnClickSignedIn = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isMin1280pxWide()) {
      notifyNavAnalytics('mv', 'Account');
    }
    if (isTouchInputDevice() && isMin1280pxWide()) {
      toggleMenu(e);
    }
  };

  const handleFocus = () => {
    if (!isTouchInputDevice()) {
      openMenu();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLLIElement>) => {
    if (isTouchInputDevice()) {
      return;
    }

    const nextFocusTarget = e.relatedTarget;

    if (
      !nextFocusTarget ||
      !e.currentTarget.contains(nextFocusTarget as Node)
    ) {
      closeMenu();
    }
  };

  const handleMouseEnter = () => {
    if (!isTouchInputDevice()) {
      openMenu();
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchInputDevice()) {
      closeMenu();
    }
  };

  const formattedGreeting = formatGreeting(greeting);
  const displayedGreeting =
    formattedGreeting === '' ? 'My account' : `Hello ${formattedGreeting}`;
  const accountLinks = getAccountLinks(
    accountBaseUrl,
    myAccountUrl,
    notifications?.hasNotification
  );

  const wishListUrl = !signedIn
    ? `/wish-list/guest`
    : `${accountBaseUrl}/wish-list`;

  const signInUrl = `/sign-in`;
  const registerUrl = `/register`;

  return (
    <>
      {!shouldHideAccount && (
        <>
          {signedIn ? (
            <li
              className={styles.accountButton}
              data-test="menu-triggers"
              data-testid="menu-trigger"
              onFocusCapture={handleFocus}
              onBlurCapture={handleBlur}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div
                className={styles.account}
                data-testid="account-button-container"
              >
                <AccountButton
                  onClick={handleOnClickSignedIn}
                  greeting={displayedGreeting}
                  hasNotification={notifications?.hasNotification}
                  myAccountUrl={myAccountUrl}
                />
                <AccountMenu isOpen={isMenuOpen} accountLinks={accountLinks} />
              </div>
            </li>
          ) : (
            <>
              <li
                className={classNames(styles.signIn, lgOnlyClassname)}
                data-testid="account-sign-in-url"
              >
                <a href="/sign-in">Sign in</a>
              </li>
              <li className={styles.accountButton}>
                <AccountButton
                  onClick={handleOnClickSignedOut}
                  myAccountUrl={myAccountUrl}
                />
              </li>
            </>
          )}
        </>
      )}
      {!shouldHideWishList && (
        <AccountWishList
          wishListUrl={wishListUrl}
          signInUrl={signInUrl}
          registerUrl={registerUrl}
          hasNotification={notifications?.hasNotification}
          signedIn={signedIn}
          lgOnlyClassname={lgOnlyClassname}
        />
      )}
    </>
  );
};
